// NOTE: NSAI-3847 — a minimal, streaming ZIP writer so a whole meeting
// (recording + summary + transcript) can be saved as one file on web and
// mobile alike.
//
// Why hand-rolled rather than a zip library: the only thing needed here is the
// STORE method — a meeting recording is already-compressed WebM/MP4/WAV-in-a-
// container, so deflating it burns CPU for ~nothing — and the container format
// for stored entries is small and fixed. That keeps `frontend-shared` free of a
// new runtime dependency (see its CLAUDE.md allow-list) and keeps the writer
// isomorphic: no DOM, no Node, no TextEncoder requirement.
//
// Why *streaming*: the recording must never exist twice in memory. Bytes are
// pushed through `writeStreamedChunk` and handed to `onChunk` immediately, so
// the caller decides where they go — accumulated for a Blob on web, appended to
// a file handle on mobile — and the source chunk can be dropped right after.
//
// Streamed entries are written in ZIP64 form unconditionally: their size is
// unknown when the local header goes out (that is the point of streaming) and an
// uploaded meeting video can exceed the 4 GiB that classic ZIP fields hold.
// Every reader that matters (Windows Explorer, macOS Archive Utility, `unzip`,
// 7-Zip, Python's `zipfile`) has read ZIP64 for years.

import type {
  IAddZipTextFileProps,
  ICreateZipStreamProps,
  IStartZipStreamedFileProps,
  IZipStream,
} from "../types/zip-writer";

const ZIP_LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;
const ZIP_DATA_DESCRIPTOR_SIGNATURE = 0x08074b50;
const ZIP_CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const ZIP64_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06064b50;
const ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIGNATURE = 0x07064b50;

const ZIP64_EXTRA_FIELD_ID = 0x0001;
const ZIP_STORE_METHOD = 0;

// NOTE: Bit 3 — sizes and CRC follow the data in a descriptor; bit 11 — the
// filename is UTF-8, which meeting titles very much can be.
const ZIP_DATA_DESCRIPTOR_FLAG = 0x0008;
const ZIP_UTF8_NAME_FLAG = 0x0800;

const ZIP_VERSION_STORE = 20;
const ZIP_VERSION_ZIP64 = 45;

// NOTE: "Version made by" is a host-system byte over a spec-version byte, and
// the host byte is load-bearing: Info-ZIP `unzip` OEM-translates entry names it
// believes were written on an MS-DOS/FAT host (byte 0), which mangles UTF-8
// names — a meeting titled in Cyrillic or Arabic extracts as mojibake — even
// though the UTF-8 name flag is set. Declaring the Unix host (3) stops that
// translation. Python's zipfile reads either way, which is why only a real
// `unzip` shows the difference.
const ZIP_UNIX_HOST_SYSTEM = 3;
const ZIP_VERSION_MADE_BY = (ZIP_UNIX_HOST_SYSTEM << 8) | ZIP_VERSION_ZIP64;

const ZIP_UINT16_MAX = 0xffff;
const ZIP_UINT32_MAX = 0xffffffff;

const ZIP_LOCAL_FILE_HEADER_SIZE = 30;
const ZIP_CENTRAL_DIRECTORY_HEADER_SIZE = 46;
const ZIP64_LOCAL_EXTRA_FIELD_SIZE = 20;
const ZIP64_DATA_DESCRIPTOR_SIZE = 24;
const ZIP64_END_OF_CENTRAL_DIRECTORY_SIZE = 56;
const ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIZE = 20;
const ZIP_END_OF_CENTRAL_DIRECTORY_SIZE = 22;

const BYTES_PER_UINT32 = 0x100000000;

// NOTE: The DOS timestamp ZIP stores has two-second resolution and starts at
// 1980; anything earlier clamps to the epoch it can express.
const DOS_EPOCH_YEAR = 1980;

const buildCrc32Table = (): Uint32Array => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }

  return table;
};

const CRC32_TABLE = buildCrc32Table();

const updateCrc32 = (crc: number, bytes: Uint8Array<ArrayBuffer>): number => {
  let nextCrc = crc;

  for (let index = 0; index < bytes.length; index += 1) {
    nextCrc =
      // NOTE: `?? 0` only satisfies the index signature — both operands are
      // always in range.
      (CRC32_TABLE[(nextCrc ^ (bytes[index] ?? 0)) & 0xff] ?? 0) ^
      (nextCrc >>> 8);
  }

  return nextCrc >>> 0;
};

// NOTE: Explicit UTF-8 encoding rather than `TextEncoder`: Hermes does not
// guarantee the global (see getUtf8ByteLength in socket-payload-size.ts), and a
// filename or a transcript byte count that is merely *approximate* would
// corrupt the archive rather than mis-size a estimate.
const encodeUtf8 = (text: string): Uint8Array<ArrayBuffer> => {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(text);
  }

  const bytes: number[] = [];

  for (const character of text) {
    const codePoint = character.codePointAt(0) ?? 0;

    if (codePoint < 0x80) {
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint < 0x10000) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }

  return Uint8Array.from(bytes);
};

interface IByteWriter {
  bytes: Uint8Array<ArrayBuffer>;
  offset: number;
}

const createByteWriter = (size: number): IByteWriter => ({
  bytes: new Uint8Array(size),
  offset: 0,
});

const writeUint16 = (writer: IByteWriter, value: number): void => {
  writer.bytes[writer.offset] = value & 0xff;
  writer.bytes[writer.offset + 1] = (value >>> 8) & 0xff;
  writer.offset += 2;
};

const writeUint32 = (writer: IByteWriter, value: number): void => {
  writer.bytes[writer.offset] = value & 0xff;
  writer.bytes[writer.offset + 1] = (value >>> 8) & 0xff;
  writer.bytes[writer.offset + 2] = (value >>> 16) & 0xff;
  writer.bytes[writer.offset + 3] = (value >>> 24) & 0xff;
  writer.offset += 4;
};

// NOTE: Sizes and offsets are plain numbers (safe up to 2^53 — 9 PB, far past
// anything a meeting produces), split into two 32-bit halves on the way out.
const writeUint64 = (writer: IByteWriter, value: number): void => {
  writeUint32(writer, value % BYTES_PER_UINT32);
  writeUint32(writer, Math.floor(value / BYTES_PER_UINT32));
};

const writeBytes = (
  writer: IByteWriter,
  bytes: Uint8Array<ArrayBuffer>,
): void => {
  writer.bytes.set(bytes, writer.offset);
  writer.offset += bytes.length;
};

interface IDosTimestamp {
  time: number;
  date: number;
}

const toDosTimestamp = (value: Date): IDosTimestamp => {
  // NOTE: A date the DOS format cannot express clamps to the whole epoch
  // instant (1980-01-01 00:00:00), not just to the year — carrying the
  // original month/day/time onto a clamped year would invent a timestamp.
  if (value.getFullYear() < DOS_EPOCH_YEAR || Number.isNaN(value.getTime())) {
    return { time: 0, date: (1 << 5) | 1 };
  }

  return {
    time:
      (value.getHours() << 11) |
      (value.getMinutes() << 5) |
      Math.floor(value.getSeconds() / 2),
    date:
      ((value.getFullYear() - DOS_EPOCH_YEAR) << 9) |
      ((value.getMonth() + 1) << 5) |
      value.getDate(),
  };
};

interface ICentralDirectoryEntry {
  nameBytes: Uint8Array<ArrayBuffer>;
  flags: number;
  crc32: number;
  size: number;
  localHeaderOffset: number;
  isZip64: boolean;
}

interface IStreamedEntryState {
  nameBytes: Uint8Array<ArrayBuffer>;
  crc32: number;
  size: number;
  localHeaderOffset: number;
}

export const createZipStream = ({
  onChunk,
  modifiedAt = new Date(),
}: ICreateZipStreamProps): IZipStream => {
  const { time: dosTime, date: dosDate } = toDosTimestamp(modifiedAt);

  const entries: ICentralDirectoryEntry[] = [];
  let bytesWritten = 0;
  let streamedEntry: IStreamedEntryState | null = null;

  const emit = (chunk: Uint8Array<ArrayBuffer>): void => {
    bytesWritten += chunk.length;
    onChunk(chunk);
  };

  const writeLocalFileHeader = ({
    nameBytes,
    flags,
    crc32,
    size,
    isZip64,
  }: {
    nameBytes: Uint8Array<ArrayBuffer>;
    flags: number;
    crc32: number;
    size: number;
    isZip64: boolean;
  }): void => {
    const extraFieldSize = isZip64 ? ZIP64_LOCAL_EXTRA_FIELD_SIZE : 0;
    const writer = createByteWriter(
      ZIP_LOCAL_FILE_HEADER_SIZE + nameBytes.length + extraFieldSize,
    );

    writeUint32(writer, ZIP_LOCAL_FILE_HEADER_SIGNATURE);
    writeUint16(writer, isZip64 ? ZIP_VERSION_ZIP64 : ZIP_VERSION_STORE);
    writeUint16(writer, flags);
    writeUint16(writer, ZIP_STORE_METHOD);
    writeUint16(writer, dosTime);
    writeUint16(writer, dosDate);
    writeUint32(writer, crc32);
    // NOTE: With the data-descriptor flag set, APPNOTE 4.4.9 has the local
    // header carry ZERO for the CRC and both sizes — the real values follow the
    // data. Writing the 0xFFFFFFFF "see the ZIP64 extra" marker instead would
    // point a reader at the extra field beside it, which holds placeholder
    // zeros at this point, so a reader trusting the local header over the
    // central directory would report a 0-byte recording. The 8-byte width of
    // the descriptor is signalled by the ZIP64 extra field's presence, not by
    // these fields.
    writeUint32(writer, size);
    writeUint32(writer, size);
    writeUint16(writer, nameBytes.length);
    writeUint16(writer, extraFieldSize);
    writeBytes(writer, nameBytes);

    if (isZip64) {
      // NOTE: Placeholder sizes — the real ones travel in the data descriptor
      // once the stream ends, which is what makes the entry streamable.
      writeUint16(writer, ZIP64_EXTRA_FIELD_ID);
      writeUint16(writer, ZIP64_LOCAL_EXTRA_FIELD_SIZE - 4);
      writeUint64(writer, 0);
      writeUint64(writer, 0);
    }

    emit(writer.bytes);
  };

  const addTextFile = ({ name, content }: IAddZipTextFileProps): void => {
    // NOTE: Writing an entry while a streamed one is open would splice its
    // bytes into the middle of that entry's data — a silently corrupt archive
    // rather than an error. No current caller does it (text entries go in
    // before the stream starts), so this is future-proofing, and it is the
    // mirror of the guards writeStreamedChunk/endStreamedFile already carry.
    if (streamedEntry) {
      throw new Error("zip_stream_open_entry");
    }

    const nameBytes = encodeUtf8(name);
    const contentBytes = encodeUtf8(content);
    const localHeaderOffset = bytesWritten;
    const crc32 = updateCrc32(0xffffffff, contentBytes) ^ 0xffffffff;

    writeLocalFileHeader({
      nameBytes,
      flags: ZIP_UTF8_NAME_FLAG,
      crc32: crc32 >>> 0,
      size: contentBytes.length,
      isZip64: false,
    });
    emit(contentBytes);

    entries.push({
      nameBytes,
      flags: ZIP_UTF8_NAME_FLAG,
      crc32: crc32 >>> 0,
      size: contentBytes.length,
      localHeaderOffset,
      isZip64: false,
    });
  };

  const startStreamedFile = ({ name }: IStartZipStreamedFileProps): void => {
    // NOTE: One streamed entry at a time — see addTextFile.
    if (streamedEntry) {
      throw new Error("zip_stream_open_entry");
    }

    const nameBytes = encodeUtf8(name);
    const localHeaderOffset = bytesWritten;

    writeLocalFileHeader({
      nameBytes,
      flags: ZIP_UTF8_NAME_FLAG | ZIP_DATA_DESCRIPTOR_FLAG,
      crc32: 0,
      size: 0,
      isZip64: true,
    });

    streamedEntry = {
      nameBytes,
      crc32: 0xffffffff,
      size: 0,
      localHeaderOffset,
    };
  };

  const writeStreamedChunk = (chunk: Uint8Array<ArrayBuffer>): void => {
    if (!streamedEntry) {
      throw new Error("zip_stream_no_open_entry");
    }

    streamedEntry.crc32 = updateCrc32(streamedEntry.crc32, chunk);
    streamedEntry.size += chunk.length;
    emit(chunk);
  };

  const endStreamedFile = (): void => {
    if (!streamedEntry) {
      throw new Error("zip_stream_no_open_entry");
    }

    const crc32 = (streamedEntry.crc32 ^ 0xffffffff) >>> 0;
    const writer = createByteWriter(ZIP64_DATA_DESCRIPTOR_SIZE);

    writeUint32(writer, ZIP_DATA_DESCRIPTOR_SIGNATURE);
    writeUint32(writer, crc32);
    writeUint64(writer, streamedEntry.size);
    writeUint64(writer, streamedEntry.size);
    emit(writer.bytes);

    entries.push({
      nameBytes: streamedEntry.nameBytes,
      flags: ZIP_UTF8_NAME_FLAG | ZIP_DATA_DESCRIPTOR_FLAG,
      crc32,
      size: streamedEntry.size,
      localHeaderOffset: streamedEntry.localHeaderOffset,
      isZip64: true,
    });

    streamedEntry = null;
  };

  const writeCentralDirectoryHeader = (
    entry: ICentralDirectoryEntry,
  ): number => {
    // NOTE: An entry needs ZIP64 in the directory when it was streamed, when it
    // outgrew a 32-bit size, or when it sits past 4 GiB into the archive.
    const isSizeMasked = entry.isZip64 || entry.size > ZIP_UINT32_MAX;
    const isOffsetMasked = entry.localHeaderOffset > ZIP_UINT32_MAX;
    const zip64ValueCount = (isSizeMasked ? 2 : 0) + (isOffsetMasked ? 1 : 0);
    const extraFieldSize = zip64ValueCount > 0 ? 4 + zip64ValueCount * 8 : 0;

    const writer = createByteWriter(
      ZIP_CENTRAL_DIRECTORY_HEADER_SIZE +
        entry.nameBytes.length +
        extraFieldSize,
    );

    writeUint32(writer, ZIP_CENTRAL_DIRECTORY_SIGNATURE);
    writeUint16(writer, ZIP_VERSION_MADE_BY);
    writeUint16(
      writer,
      zip64ValueCount > 0 ? ZIP_VERSION_ZIP64 : ZIP_VERSION_STORE,
    );
    writeUint16(writer, entry.flags);
    writeUint16(writer, ZIP_STORE_METHOD);
    writeUint16(writer, dosTime);
    writeUint16(writer, dosDate);
    writeUint32(writer, entry.crc32);
    writeUint32(writer, isSizeMasked ? ZIP_UINT32_MAX : entry.size);
    writeUint32(writer, isSizeMasked ? ZIP_UINT32_MAX : entry.size);
    writeUint16(writer, entry.nameBytes.length);
    writeUint16(writer, extraFieldSize);
    // NOTE: comment length, disk number, internal attributes, external
    // attributes — all unused here.
    writeUint16(writer, 0);
    writeUint16(writer, 0);
    writeUint16(writer, 0);
    writeUint32(writer, 0);
    writeUint32(
      writer,
      isOffsetMasked ? ZIP_UINT32_MAX : entry.localHeaderOffset,
    );
    writeBytes(writer, entry.nameBytes);

    if (zip64ValueCount > 0) {
      writeUint16(writer, ZIP64_EXTRA_FIELD_ID);
      writeUint16(writer, zip64ValueCount * 8);
      // NOTE: Order is fixed by the spec: uncompressed size, compressed size,
      // then the local header offset — each present only when its 32-bit slot
      // above was masked.
      if (isSizeMasked) {
        writeUint64(writer, entry.size);
        writeUint64(writer, entry.size);
      }
      if (isOffsetMasked) {
        writeUint64(writer, entry.localHeaderOffset);
      }
    }

    emit(writer.bytes);
    return writer.bytes.length;
  };

  const writeZip64EndOfCentralDirectory = ({
    centralDirectoryOffset,
    centralDirectorySize,
  }: {
    centralDirectoryOffset: number;
    centralDirectorySize: number;
  }): void => {
    const writer = createByteWriter(
      ZIP64_END_OF_CENTRAL_DIRECTORY_SIZE +
        ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIZE,
    );
    const endOfCentralDirectoryOffset =
      centralDirectoryOffset + centralDirectorySize;

    writeUint32(writer, ZIP64_END_OF_CENTRAL_DIRECTORY_SIGNATURE);
    // NOTE: Size of this record, excluding its 12-byte signature+size prefix.
    writeUint64(writer, ZIP64_END_OF_CENTRAL_DIRECTORY_SIZE - 12);
    writeUint16(writer, ZIP_VERSION_ZIP64);
    writeUint16(writer, ZIP_VERSION_ZIP64);
    writeUint32(writer, 0);
    writeUint32(writer, 0);
    writeUint64(writer, entries.length);
    writeUint64(writer, entries.length);
    writeUint64(writer, centralDirectorySize);
    writeUint64(writer, centralDirectoryOffset);

    writeUint32(writer, ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIGNATURE);
    writeUint32(writer, 0);
    writeUint64(writer, endOfCentralDirectoryOffset);
    writeUint32(writer, 1);

    emit(writer.bytes);
  };

  const finish = (): void => {
    if (streamedEntry) {
      throw new Error("zip_stream_open_entry");
    }

    const centralDirectoryOffset = bytesWritten;
    let centralDirectorySize = 0;
    for (const entry of entries) {
      centralDirectorySize += writeCentralDirectoryHeader(entry);
    }

    // NOTE: ZIP64 tail whenever a classic end record could not describe the
    // archive faithfully — any streamed entry, or a directory that starts past
    // 4 GiB. The classic record still follows it with the saturated markers, so
    // readers that only know the old format still find something valid.
    const isZip64Archive =
      entries.some((entry) => entry.isZip64) ||
      centralDirectoryOffset > ZIP_UINT32_MAX ||
      centralDirectorySize > ZIP_UINT32_MAX ||
      entries.length > ZIP_UINT16_MAX;

    if (isZip64Archive) {
      writeZip64EndOfCentralDirectory({
        centralDirectoryOffset,
        centralDirectorySize,
      });
    }

    const writer = createByteWriter(ZIP_END_OF_CENTRAL_DIRECTORY_SIZE);
    const entryCount = Math.min(entries.length, ZIP_UINT16_MAX);

    writeUint32(writer, ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE);
    writeUint16(writer, 0);
    writeUint16(writer, 0);
    writeUint16(writer, entryCount);
    writeUint16(writer, entryCount);
    writeUint32(writer, Math.min(centralDirectorySize, ZIP_UINT32_MAX));
    writeUint32(writer, Math.min(centralDirectoryOffset, ZIP_UINT32_MAX));
    writeUint16(writer, 0);

    emit(writer.bytes);
  };

  return {
    addTextFile,
    startStreamedFile,
    writeStreamedChunk,
    endStreamedFile,
    finish,
  };
};
